import React from 'react';
import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import KenColors from '../../Helpers/Themes/kenColors';

export const useStyles = makeStyles((theme: Theme) => ({
  /* ---------- FULL SCREEN LAYOUT (same as Login) ---------- */
  container: {
    height: '100vh',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    backgroundImage: `url(${theme.palette.KenColors?.AuthLayoutBg || ''})`, // optional, often overridden by img
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    '&::before': {
      content: '""',
      position: 'absolute',
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0,0,0,0.6)', // Dark overlay
      zIndex: 1,
    },
  },
  fullScreenImageContainer: {
    position: 'relative',
    width: '100%',
    height: '100vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'row',
  },
  fullScreenImage: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    zIndex: 0,
  },
  boxLayout: {
    position: 'relative',
    zIndex: 2,
    width: '50%',
    paddingLeft: '10%',
    paddingTop: '20%',
    color: '#fff',
    [theme.breakpoints.down('md')]: {
      display: 'none', // Hide left content on tablets/mobiles
    },
  },
  rightSection: {
    position: 'relative',
    zIndex: 2,
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    width: '50%',
    paddingRight: '5%',
    [theme.breakpoints.down('md')]: {
      width: '100%',
      paddingRight: 0,
      justifyContent: 'center',
    },
  },
  loginCard: {
    backgroundColor: theme.palette.KenColors.kenWhite || '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
    padding: theme.spacing(4),
    width: '100%',
    maxWidth: '600px',
    minHeight: '80vh',
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(3),
      margin: theme.spacing(2),
    },
  },
  logoContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-start',
    marginBottom: theme.spacing(2),
  },

  /* ---------- LEFT HERO TYPOGRAPHY (same as Login) ---------- */
  heading: {
    fontSize: '40px !important',
    fontWeight: '600 !important',
    fontFamily: 'GeneralSans',
    color: theme.palette.KenColors.kenWhite,
    textTransform: 'capitalize',
    textAlign: 'left',
    paddingTop: '20px !important',
    width: '76%',
  },
  subHeading: {
    fontSize: '16px !important',
    fontWeight: '500 !important',
    color: theme.palette.KenColors.kenWhite,
    textAlign: 'left',
    width: '46%',
    marginTop: '40px !important',
  },
  layoutBar: {
    background: '#D77A36',
    height: '9px',
    width: '98px',
    margin: '10px 0px',
    marginTop: '40px !important',
  },

  /* ---------- SHARED BUTTON / INPUT / TEXT STYLES ---------- */
  loginButton: {
    width: '100%',
    borderRadius: '8px !important',
    margin: '8px auto !important',
    border: `2px solid ${theme.palette.KenColors.neural500} !important`,
    background: `${theme.palette.KenColors.kenWhite} !important`,
    boxShadow: `0px 18px 30px 0px ${theme.palette.KenColors.ClientShadow}`,
    padding: '9px 35px !important',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    '&:hover': {
      background: `${theme.palette.KenColors.kenWhite} !important`,
    },
    '&:active': {
      background: `${theme.palette.KenColors.kenWhite} !important`,
    },
  },
  resend: {
    border: 'none',
    fontSize: '12px',
    fontWeight: '500 !important',
    color: '#2C58E8',
    cursor: 'pointer',
    marginTop: '20px !important',
  },
  footerSection: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '80%',
    marginTop: '60px',
  },
  backButton: {
    color: '#373A45',
    fontWeight: '500 !important',
    fontSize: '12px',
    cursor: 'pointer',
    textDecoration: 'underline',
    marginTop: '10px',
  },

  /* ---------- OTP / CARD ---------- */
  otpContainerCard: {
    padding: '31px 90px',
    borderRadius: '20px !important',
    marginTop: '20px',
    height: '108px',
    width: '360px',
    boxShadow: 'none !important',
  },
  backIcon: {
    height: '26px',
    width: 'auto',
    color: theme.palette.KenColors.clientBlackBackground,
  },
  otpHeader: {
    display: 'flex',
    position: 'relative',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },

  /* ---------- FORM LAYOUT ---------- */
  formLayout: {
    textAlign: 'center',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: '0px !important',
    width: '100%',
    height: '100%',
  },
  formContainerLayout: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    width: '100%',
    gap: theme.spacing(2),
    height: '100%',
  },
  fieldLayout: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    gap: theme.spacing(2),
  },

  /* ---------- ICONS / LOGO ---------- */
  loginIcons: {
    height: '20px',
    width: '20px',
  },
  logo: {
    height: '72px',
    width: '72px',
    objectFit: 'cover',
  },

  /* ---------- TEXT / TITLES ---------- */
  loginText: {
    fontWeight: '600 !important',
    fontSize: '12px !important',
    color: theme.palette.KenColors.clientBlackBackground,
  },
  title: {
    marginTop: 0,
    marginBottom: theme.spacing(3),
    fontWeight: '600 !important',
    fontSize: '20px !important',
    textAlign: 'center',
  },
  titleOtp: {
    textAlign: 'center',
    fontSize: '15px',
    fontWeight: '500 !important',
    color: theme.palette.KenColors.clientBlackBackground,
    marginBottom: '20px !important',
  },
  titleVerified: {
    textAlign: 'center',
    fontSize: '18px',
    fontWeight: '500 !important',
    color: theme.palette.KenColors.clientBlackBackground,
    marginBottom: '20px !important',
  },
  descriptionVerified: {
    textAlign: 'center',
    fontSize: '15px',
    fontWeight: '400 !important',
    color: theme.palette.KenColors.clientBlackBackground,
    marginBottom: '20px !important',
  },

  inputFieldLabel: {
    fontWeight: '500 !important',
    fontSize: '16px !important',
    color: theme.palette.KenColors.clientCLight,
  },
  subTitle: {
    fontSize: '14px',
    fontWeight: '500 !important',
    color: `${theme.palette.KenColors.clientError} !important`,
    fontFamily: 'GeneralSans !important',
  },

  /* ---------- BUTTONS ---------- */
  verify: {
    fontSize: '16px !important',
    fontWeight: '600 !important',
    background: `${theme.palette.KenColors.ClientPrimary} !important`,
    textTransform: 'none',
    color: `${theme.palette.KenColors.kenWhite} !important`,
    borderRadius: '8px !important',
    height: '36px',
    width: '100%',
    padding: '9px !important',
  },
  verify1: {
    fontSize: '12px !important',
    fontWeight: '500 !important',
    background: `${theme.palette.KenColors.ClientPrimary} !important`,
    textTransform: 'none',
    color: `${theme.palette.KenColors.kenWhite} !important`,
    borderRadius: '8px !important',
    width: '90px',
    height: '36px',
    padding: '9px !important',
    display: 'flex',
    justifyContent: 'center',
    marginTop: '10px !important',
  },

  /* ---------- INPUTS & LABELS ---------- */
  inputStyles: {
    height: '36px !important',
    fontSize: '12px !important',
    borderRadius: '6px !important',
    backgroundColor: '#FFFFFF',
  },
  inputStyles1: {
    height: '36px !important',
    width: '333px !important',
    fontSize: '12px !important',
    borderRadius: '6px !important',
    backgroundColor: '#FFFFFF',
    marginLeft: '40px !important',
  },
  label: {
    marginBottom: theme.spacing(0.5),
    fontSize: '14px',
    fontWeight: 500,
    textAlign: 'left',
  },
  label1: {
    fontWeight: '500 !important',
    fontSize: '12px !important',
    textAlign: 'left',
    margin: '4px 0px 5px 0px !important',
    color: theme.palette.KenColors.clientBlackBackground,
    fontFamily: 'GeneralSans !important',
    marginLeft: '40px !important',
  },

  /* ---------- ACCOUNT LINK ---------- */
  accountLink: {
    textAlign: 'center',
    fontSize: '14px',
    bottom: '0 !important',
    color: theme.palette.KenColors.clientBlackBackground,
    fontFamily: 'GeneralSans !important',
  },

  /* ---------- PHONE INPUT ---------- */
  phoneInputStyles: {
    textAlign: 'start',
    borderRadius: '0px 8px 8px 0px !important',
    color: theme.palette.KenColors.ClientGrey,
    margin: '0px !important',
    padding: '12px !important',
    width: '100% !important',
    background: `${theme.palette.KenColors.kenWhite} !important`,
    border: `2px solid ${theme.palette.KenColors.grey} !important`,
    borderLeft: '0px !important',
    height: '36px !important',
  },
  countryCodeError: {
    background: theme.palette.KenColors.kenWhite,
    textAlign: 'start',
    margin: '0px',
    padding: '0px',
    borderRadius: '10px 0px 0px 10px !important',
    border: '2px solid red !important',
    color: theme.palette.KenColors.clientBlackBackground,
    '&:hover': {
      border: '2px solid red !important',
      borderRadius: '10px 0px 0px 10px !important',
    },
    '&.open': {
      background: theme.palette.KenColors.kenWhite,
      '& .selected-flag': {
        background: theme.palette.KenColors.kenWhite,
      },
    },
    '& .selected-flag': {
      '&:hover': {
        background: theme.palette.KenColors.kenWhite,
      },
    },
  },
  phoneError: {
    border: '2px solid red !important',
    borderLeft: '0px !important',
  },
  countryCode: {
    background: theme.palette.KenColors.kenWhite,
    textAlign: 'start',
    margin: '0px',
    padding: '0px',
    borderRadius: '8px 0px 0px 8px !important',
    border: `2px solid ${theme.palette.KenColors.grey} !important`,
    color: theme.palette.KenColors.clientBlackBackground,
    '&:hover': {
      border: `2px solid ${theme.palette.KenColors.grey} !important`,
      borderRadius: '7px 0px 0px 7px !important',
    },
    '&.open': {
      background: theme.palette.KenColors.kenWhite,
      '& .selected-flag': {
        background: theme.palette.KenColors.kenWhite,
      },
    },
    '& .selected-flag': {
      '&:hover': {
        background: theme.palette.KenColors.kenWhite,
      },
    },
  },

  /* ---------- OTHER CARDS ---------- */
  confirmMailContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
  },
  cardContainer: {
    borderRadius: '12px !important',
    width: '360px',
    marginTop: '20px',
    height: '135px',
    boxShadow: 'none !important',
  },
  otpCard: {
    boxShadow: 'none !important',
    borderRadius: '15px !important',
    padding: '25px',
    display: 'flex',
    justifyContent: 'center',
  },
  verifyCard: {
    boxShadow: 'none !important',
    borderRadius: '15px !important',
    padding: '25px',
  },
  otpInput: {
    width: '50px',
    height: '58px',
    border: '1px solid #A0B6FF !important',
    borderRadius: '10px !important',
    backgroundColor: '#EAEFFF',
  },
  otpInput1: {
    width: '50px',
    height: '58px',
    border: '1px solid #A0B6FF !important',
    borderRadius: '10px !important',
    backgroundColor: '#EAEFFF',
    marginLeft: '15px !important',
  },
  emailText: {
    color: '#373A45',
    textDecoration: 'underline',
    textAlign: 'center',
    marginTop: '43px !important',
    fontWeight: '500 !important',
  },
  verifyButton: {
    fontSize: '16px !important',
    fontWeight: '600 !important',
    background: `${theme.palette.KenColors.ClientPrimary} !important`,
    textTransform: 'none',
    color: `${theme.palette.KenColors.kenWhite} !important`,
    borderRadius: '8px !important',
    width: '50%',
    height: '45px',
    padding: '12px !important',
    marginTop: '40px !important',
  },
}));

export const otpInputStyle = {
  border: `1px solid #A0B6FF`,
  borderRadius: '6px',
  width: '36px',
  height: '48px',
  fontSize: '16px',
  margin: '0 8px',
  textAlign: 'center',
  justifyContent: 'center',
  backgroundColor: '#EAEFFF',
} as React.CSSProperties;

export const otpContainerStyle = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
} as React.CSSProperties;
